import React, { useEffect, useState, useCallback, useRef } from 'react';

const sectionStyle = { background: 'var(--theme-bg-light)', border: '1px solid var(--theme-border)', borderRadius: 8, padding: '1.2rem 1.4rem', marginTop: '1.2rem' };

// Placeholder list mirrors the Pre-Check-In Welcome template pattern (double
// braces). Variables are rendered server-side at check-in.
const SMS_PLACEHOLDERS = [
  { key: '{{guest_name}}', label: 'Guest Name' },
  { key: '{{hotel_name}}', label: 'Hotel Name' },
  { key: '{{reservation_no}}', label: 'Reservation No' },
  { key: '{{room_number}}', label: 'Room Number' },
  { key: '{{portal_url}}', label: 'Guest Portal Link' },
];

const DEFAULT_SMS_TEMPLATE = 'Welcome to {{hotel_name}}, {{guest_name}}! Reservation {{reservation_no}}, Room {{room_number}}. Access room services here: {{portal_url}}';
const toggleStyle = (active) => ({
  width: 44, height: 24, borderRadius: 12, background: active ? '#166534' : '#cbd5e1',
  position: 'relative', cursor: 'pointer', transition: 'background 0.2s', border: 'none', padding: 0, display: 'inline-flex', alignItems: 'center'
});
const toggleKnob = (active) => ({
  width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute',
  top: 3, left: active ? 23 : 3, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
});

function PmsGuestPortalConfig({ token, selectedProperty }) {
  const [portalEnabled, setPortalEnabled] = useState(false);
  const [authMode, setAuthMode] = useState('STANDARD'); // ER-MOM-02: STANDARD | ROOM_NUMBER_ONLY
  const [nlpModuleEnabled, setNlpModuleEnabled] = useState(false);
  const [nlpAssistantEnabled, setNlpAssistantEnabled] = useState(false);
  const [hkServices, setHkServices] = useState([]);
  const [requestServices, setRequestServices] = useState([]);
  const [infoPages, setInfoPages] = useState([]);
  const [editingInfo, setEditingInfo] = useState(null); // info_key being edited
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  // SMS auto-send-at-checkin config (per-property)
  const [smsAutoSendOnCheckin, setSmsAutoSendOnCheckin] = useState(false);
  const [smsTemplateBody, setSmsTemplateBody] = useState(DEFAULT_SMS_TEMPLATE);
  const [smsDltTemplateId, setSmsDltTemplateId] = useState('');
  const smsTextareaRef = useRef(null);

  const headers = useCallback((withJson = false) => ({
    Authorization: `Bearer ${token}`,
    ...(withJson ? { 'Content-Type': 'application/json' } : {}),
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  }), [token, selectedProperty]);

  const load = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/v1/pms/guest-portal/config', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load config');
      setPortalEnabled(data.portal_enabled || false);
      setAuthMode(data.auth_mode || 'STANDARD');
      setNlpModuleEnabled(data.nlp_module_enabled || false);
      setNlpAssistantEnabled(data.nlp_assistant_enabled || false);
      setHkServices(data.housekeeping || []);
      setRequestServices(data.services || []);
      setSmsAutoSendOnCheckin(Boolean(data.sms_auto_send_on_checkin));
      setSmsTemplateBody(data.sms_template_body || DEFAULT_SMS_TEMPLATE);
      setSmsDltTemplateId(data.sms_dlt_template_id || '');
      // Load hotel info pages
      try {
        const infoRes = await fetch('/api/v1/pms/hotel-info', { headers: headers() });
        const infoData = await infoRes.json();
        if (infoRes.ok && Array.isArray(infoData)) setInfoPages(infoData);
      } catch { /* info pages load is optional */ }
    } catch (err) {
      setError(err?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [selectedProperty, headers]);

  useEffect(() => { load(); }, [load]);

  const toggleHk = (idx) => {
    setHkServices(prev => prev.map((s, i) => i === idx ? { ...s, is_active: !s.is_active } : s));
  };

  const toggleRequest = (idx) => {
    setRequestServices(prev => prev.map((s, i) => i === idx ? { ...s, is_active: !s.is_active } : s));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const allServices = [
        ...hkServices.map((s, i) => ({ ...s, display_order: i })),
        ...requestServices.map((s, i) => ({ ...s, display_order: i }))
      ];
      const res = await fetch('/api/v1/pms/guest-portal/config', {
        method: 'POST',
        headers: headers(true),
        body: JSON.stringify({
          portal_enabled: portalEnabled,
          auth_mode: authMode,
          nlp_assistant_enabled: nlpAssistantEnabled,
          services: allServices,
          sms_auto_send_on_checkin: smsAutoSendOnCheckin,
          sms_template_body: smsTemplateBody,
          sms_dlt_template_id: smsDltTemplateId,
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to save');
      // Save hotel info pages
      for (const page of infoPages) {
        await fetch(`/api/v1/pms/hotel-info/${page.info_key}`, {
          method: 'PUT',
          headers: headers(true),
          body: JSON.stringify({ title: page.title, content: page.content, icon_name: page.icon_name, display_order: page.display_order, is_active: page.is_active })
        });
      }
      setEditingInfo(null);
      setMessage('Guest Portal configuration saved successfully');
      setTimeout(() => setMessage(''), 4000);
    } catch (err) {
      setError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleInfoActive = (idx) => {
    setInfoPages(prev => prev.map((p, i) => i === idx ? { ...p, is_active: !p.is_active } : p));
  };

  const updateInfoField = (idx, field, value) => {
    setInfoPages(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  // Insert a placeholder at the textarea cursor (or append). Mirrors
  // PmsPreCheckinConfig.insertPlaceholder.
  const insertSmsPlaceholder = (ph) => {
    const el = smsTextareaRef.current;
    if (!el || typeof el.selectionStart !== 'number') {
      setSmsTemplateBody((prev) => `${prev || ''}${ph}`);
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    setSmsTemplateBody((prev) => {
      const text = prev || '';
      const next = text.slice(0, start) + ph + text.slice(end);
      // restore caret after the inserted placeholder on next paint
      setTimeout(() => {
        try {
          el.focus();
          el.selectionStart = el.selectionEnd = start + ph.length;
        } catch { /* noop */ }
      }, 0);
      return next;
    });
  };

  // Sample preview rendered with stub values, just like the Pre-Check-In config.
  const smsPreview = (() => {
    const sample = {
      guest_name: 'Mr Kumar',
      hotel_name: 'Hotel Cute Orange',
      reservation_no: 'RES-1234',
      room_number: '203',
      portal_url: 'https://easepms.easeroom.co/guest-portal/?code=CO',
    };
    return String(smsTemplateBody || '').replace(/\{\{(\w+)\}\}/g, (_, k) => sample[k] != null ? String(sample[k]) : `{{${k}}}`);
  })();

  const categories = ['RECEPTION', 'MAINTENANCE', 'AMENITY', 'OTHER'];
  const catLabels = { RECEPTION: 'Reception', MAINTENANCE: 'Maintenance', AMENITY: 'Amenities', OTHER: 'Other' };

  const activeHkCount = hkServices.filter(s => s.is_active).length;
  const activeReqCount = requestServices.filter(s => s.is_active).length;

  return (
    <div className="page-shell">
      <div style={{ marginBottom: '0.2rem', color: 'var(--theme-text-secondary)', fontSize: '0.82rem' }}>Hotel Config &gt; Guest Portal</div>
      <h2 style={{ margin: '0 0 0.3rem', color: 'var(--theme-primary)' }}>Guest Digital Portal Configuration</h2>
      <p style={{ color: 'var(--theme-text-secondary)', margin: '0 0 1rem', fontSize: '0.88rem' }}>
        Enable the guest portal and configure which services are available for guests.
      </p>

      {error && <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.5rem', fontSize: '0.85rem' }}>{error}</div>}
      {message && <div style={{ background: '#f0fdf4', color: '#166534', padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.5rem', fontSize: '0.85rem' }}>{message}</div>}

      {loading ? <div style={{ color: 'var(--theme-text-secondary)' }}>Loading...</div> : (
        <>
          {/* Portal Enable/Disable */}
          <div style={{ ...sectionStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--theme-primary)' }}>Guest Portal Access</div>
              <div style={{ color: 'var(--theme-text-secondary)', fontSize: '0.82rem', marginTop: '0.2rem' }}>
                {portalEnabled
                  ? 'Portal is active — guests can access services via QR code / link'
                  : 'Portal is disabled — guests will see a "not available" message'}
              </div>
            </div>
            <button onClick={() => setPortalEnabled(!portalEnabled)} style={toggleStyle(portalEnabled)}>
              <span style={toggleKnob(portalEnabled)} />
            </button>
          </div>

          {!portalEnabled && (
            <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 8, padding: '0.7rem 1rem', marginTop: '0.6rem', fontSize: '0.85rem', color: '#92400e' }}>
              Guest Portal is disabled. Enable it above to allow guests to access the portal.
            </div>
          )}

          {/* ER-MOM-02: Guest Login Validation mode */}
          {portalEnabled && (
            <div style={sectionStyle}>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--theme-primary)' }}>Guest Login Validation</div>
              <div style={{ color: 'var(--theme-text-secondary)', fontSize: '0.82rem', margin: '0.2rem 0 0.8rem' }}>
                How a guest proves they belong to a room when opening the portal.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {[
                  { value: 'STANDARD', title: 'Standard (recommended)', desc: 'Room number + guest name + last 4 digits of mobile. Highest assurance.' },
                  { value: 'ROOM_NUMBER_ONLY', title: 'Room number only', desc: 'Guest enters just the room number. Low-friction, but lower security — anyone who knows an occupied room number can open its portal. Best for trusted in-room QR access only.' },
                ].map(opt => (
                  <label key={opt.value} style={{
                    display: 'flex', gap: '0.6rem', alignItems: 'flex-start', cursor: 'pointer',
                    padding: '0.6rem 0.8rem', borderRadius: 8,
                    border: `1px solid ${authMode === opt.value ? 'var(--theme-primary)' : 'var(--theme-border)'}`,
                    background: authMode === opt.value ? 'rgba(22,101,52,0.06)' : '#fff'
                  }}>
                    <input type="radio" name="gp_auth_mode" value={opt.value}
                      checked={authMode === opt.value}
                      onChange={() => setAuthMode(opt.value)}
                      style={{ marginTop: 3 }} />
                    <span>
                      <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1f2937' }}>{opt.title}</span>
                      <span style={{ display: 'block', fontSize: '0.8rem', color: 'var(--theme-text-secondary)', marginTop: '0.15rem' }}>{opt.desc}</span>
                    </span>
                  </label>
                ))}
              </div>
              {authMode === 'ROOM_NUMBER_ONLY' && (
                <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 8, padding: '0.6rem 0.9rem', marginTop: '0.7rem', fontSize: '0.82rem', color: '#92400e' }}>
                  ⚠ Room-number-only skips identity checks. Enable only where portal access is limited to low-risk in-stay actions.
                </div>
              )}
            </div>
          )}

          {/* NLP Assistant Toggle — only visible if NLP module is enabled at admin level */}
          {nlpModuleEnabled && (
            <div style={{ ...sectionStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--theme-primary)' }}>NLP Voice/Text Assistant</div>
                <div style={{ color: 'var(--theme-text-secondary)', fontSize: '0.82rem', marginTop: '0.2rem' }}>
                  {nlpAssistantEnabled
                    ? 'Voice/text assistant is active — guests can speak or type requests'
                    : 'Voice/text assistant is disabled for guests'}
                </div>
              </div>
              <button onClick={() => setNlpAssistantEnabled(!nlpAssistantEnabled)} style={toggleStyle(nlpAssistantEnabled)}>
                <span style={toggleKnob(nlpAssistantEnabled)} />
              </button>
            </div>
          )}

          {/* Housekeeping Services */}
          <div style={sectionStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.7rem' }}>
              <div>
                <div style={{ fontWeight: 700, color: 'var(--theme-primary)' }}>Housekeeping Services</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--theme-text-secondary)' }}>{activeHkCount} of {hkServices.length} active</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.5rem' }}>
              {hkServices.map((s, idx) => (
                <div key={s.service_type} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '0.5rem 0.75rem', background: s.is_active ? '#f0fdf4' : '#fff',
                  border: `1px solid ${s.is_active ? '#bbf7d0' : 'var(--theme-border)'}`, borderRadius: 6
                }}>
                  <span style={{ fontSize: '0.88rem', fontWeight: 500, color: s.is_active ? '#1f2937' : 'var(--theme-text-muted)' }}>
                    {s.service_label || s.service_type.replace(/_/g, ' ')}
                  </span>
                  <button onClick={() => toggleHk(idx)} style={toggleStyle(s.is_active)}>
                    <span style={toggleKnob(s.is_active)} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Service Request Types */}
          <div style={sectionStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.7rem' }}>
              <div>
                <div style={{ fontWeight: 700, color: 'var(--theme-primary)' }}>Guest Service Requests</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--theme-text-secondary)' }}>{activeReqCount} of {requestServices.length} active</div>
              </div>
            </div>
            {categories.map(cat => {
              const catServices = requestServices.map((s, idx) => ({ ...s, _idx: idx })).filter(s => s.category === cat);
              if (!catServices.length) return null;
              return (
                <div key={cat} style={{ marginBottom: '0.8rem' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#475569', marginBottom: '0.4rem', borderBottom: '1px solid var(--theme-border)', paddingBottom: '0.25rem' }}>
                    {catLabels[cat] || cat}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.5rem' }}>
                    {catServices.map(s => (
                      <div key={s.service_type} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '0.5rem 0.75rem', background: s.is_active ? '#f0fdf4' : '#fff',
                        border: `1px solid ${s.is_active ? '#bbf7d0' : 'var(--theme-border)'}`, borderRadius: 6
                      }}>
                        <span style={{ fontSize: '0.88rem', fontWeight: 500, color: s.is_active ? '#1f2937' : 'var(--theme-text-muted)' }}>
                          {s.service_label || s.service_type.replace(/_/g, ' ')}
                        </span>
                        <button onClick={() => toggleRequest(s._idx)} style={toggleStyle(s.is_active)}>
                          <span style={toggleKnob(s.is_active)} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Hotel Information Pages */}
          <div style={sectionStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.7rem' }}>
              <div>
                <div style={{ fontWeight: 700, color: 'var(--theme-primary)' }}>Hotel Information Pages</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--theme-text-secondary)' }}>
                  Content shown to guests on the Hotel Information screen (WiFi, Breakfast, Policies, etc.)
                </div>
              </div>
            </div>
            {infoPages.length === 0 ? (
              <div style={{ color: 'var(--theme-text-muted)', fontSize: '0.85rem', fontStyle: 'italic' }}>
                No info pages found. They will be auto-created when a guest first accesses the portal.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {infoPages.map((page, idx) => (
                  <div key={page.info_key} style={{
                    border: `1px solid ${page.is_active ? '#bbf7d0' : 'var(--theme-border)'}`,
                    borderRadius: 8, background: page.is_active ? '#f0fdf4' : '#fff', padding: '0.75rem 1rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: editingInfo === page.info_key ? '0.6rem' : 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flex: 1 }}>
                        <span style={{ fontSize: '0.92rem', fontWeight: 600, color: page.is_active ? '#1f2937' : 'var(--theme-text-muted)' }}>
                          {page.icon_name ? `${page.icon_name}  ` : ''}{page.title || page.info_key.replace(/_/g, ' ')}
                        </span>
                        <button onClick={() => setEditingInfo(editingInfo === page.info_key ? null : page.info_key)}
                          style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 500, padding: '2px 6px' }}>
                          {editingInfo === page.info_key ? 'Collapse' : 'Edit'}
                        </button>
                      </div>
                      <button onClick={() => toggleInfoActive(idx)} style={toggleStyle(page.is_active)}>
                        <span style={toggleKnob(page.is_active)} />
                      </button>
                    </div>
                    {editingInfo === page.info_key && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <div>
                          <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '0.2rem', display: 'block' }}>Title</label>
                          <input type="text" value={page.title || ''} onChange={e => updateInfoField(idx, 'title', e.target.value)}
                            style={{ width: '100%', padding: '0.4rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: '0.88rem', boxSizing: 'border-box' }} />
                        </div>
                        <div>
                          <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '0.2rem', display: 'block' }}>Content</label>
                          <textarea value={page.content || ''} onChange={e => updateInfoField(idx, 'content', e.target.value)}
                            rows={5}
                            style={{ width: '100%', padding: '0.4rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: '0.85rem', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* SMS at Check-In — auto-send the Guest Portal link by SMS */}
          <div style={sectionStyle}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', marginBottom: '0.5rem' }}>
              <div>
                <div style={{ fontWeight: 700, color: 'var(--theme-primary)' }}>Send Guest Portal link by SMS at check-in</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--theme-text-secondary)' }}>
                  When ON, the message below is sent to the guest's mobile automatically as soon as their stay opens. When OFF, staff can still copy / WhatsApp the link from the room card.
                </div>
              </div>
              <button onClick={() => setSmsAutoSendOnCheckin(!smsAutoSendOnCheckin)} style={toggleStyle(smsAutoSendOnCheckin)}>
                <span style={toggleKnob(smsAutoSendOnCheckin)} />
              </button>
            </div>

            {/* DLT template id */}
            <div style={{ marginTop: '0.7rem', display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
              <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#475569', minWidth: 160 }}>DLT Template ID</label>
              <input
                type="text"
                value={smsDltTemplateId}
                onChange={(e) => setSmsDltTemplateId(e.target.value)}
                placeholder="e.g. 1707177651421899895"
                style={{ flex: '1 1 240px', padding: '0.4rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: '0.85rem', boxSizing: 'border-box' }}
              />
              <span style={{ fontSize: '0.74rem', color: 'var(--theme-text-secondary)' }}>(optional — required by some Indian SMS providers)</span>
            </div>

            {/* Placeholder chips + textarea */}
            <div style={{ marginTop: '0.9rem' }}>
              <div style={{ fontSize: '0.82rem', color: 'var(--theme-text-secondary)', marginBottom: '0.4rem' }}>
                Use the placeholders below to insert dynamic values. They are rendered server-side at the moment of sending.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.6rem' }}>
                {SMS_PLACEHOLDERS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => insertSmsPlaceholder(p.key)}
                    className="btn-muted"
                    style={{ padding: '0.2rem 0.55rem', fontSize: '0.74rem', borderRadius: 4 }}
                  >+ {p.label}</button>
                ))}
              </div>
              <textarea
                ref={smsTextareaRef}
                value={smsTemplateBody}
                onChange={(e) => setSmsTemplateBody(e.target.value)}
                rows={5}
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  fontSize: '0.85rem',
                  fontFamily: 'monospace',
                  borderRadius: 6,
                  border: '1px solid var(--theme-border)',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />

              <details style={{ marginTop: '0.7rem' }}>
                <summary style={{ fontSize: '0.82rem', color: 'var(--theme-primary)', cursor: 'pointer', fontWeight: 600 }}>
                  Preview with sample data
                </summary>
                <div style={{
                  marginTop: '0.4rem',
                  padding: '0.7rem 0.8rem',
                  background: '#f8fafc',
                  borderRadius: 6,
                  border: '1px solid var(--theme-border)',
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.85rem',
                  lineHeight: 1.5,
                  color: 'var(--theme-text)',
                }}>{smsPreview}</div>
              </details>

              <div style={{ marginTop: '0.6rem', display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setSmsTemplateBody(DEFAULT_SMS_TEMPLATE)}
                  className="btn-muted rounded"
                  style={{ padding: '0.4rem 0.9rem', fontSize: '0.82rem' }}
                >Reset to default</button>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div style={{ marginTop: '1.2rem', display: 'flex', gap: '0.6rem' }}>
            <button onClick={handleSave} disabled={saving}
              style={{ padding: '0.6rem 2rem', background: 'var(--theme-primary)', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: '0.9rem', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
            <button onClick={load} style={{ padding: '0.6rem 1.2rem', background: 'var(--theme-border-light)', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 6, fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>
              Reset
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default PmsGuestPortalConfig;
